"use client";

import React, { useEffect, useRef, useState } from "react";

const VideoPlayer = ({
    src,
    trackingEvents,
    onProgressChange,
    onEventFired,
}) => {
    const videoRef = useRef(null);
    const firedEvents = useRef(new Set());
    const hasStarted = useRef(false);
    const [showReplay, setShowReplay] = useState(false);

    const fireTrackingEvent = (name, url) => {
        if (!firedEvents.current.has(name)) {
            firedEvents.current.add(name);

            if (url) {
                fetch(url)
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(
                                `HTTP ${response.status} - ${response.statusText}`
                            );
                        }
                        console.log(`✅ Success: ${name} -> ${url}`);
                        if (onEventFired) onEventFired(name, "success");
                    })
                    .catch((error) => {
                        if (error.message === "Failed to fetch") {
                            console.warn(
                                `⚠️ Network error while firing event '${name}' at ${url}. Possible CORS or network issue.`
                            );
                        }
                        console.error(
                            `❌ Failed to fire event '${name}' at ${url}:`,
                            error
                        );
                        if (onEventFired) onEventFired(name, "error");
                    })
                    .finally(() => {
                        console.log(`🔁 Completed tracking event: ${name}`);
                    });
            }
        }
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;

        const current = videoRef.current.currentTime;
        const duration = videoRef.current.duration;
        if (!duration) return;

        const progress = current / duration;
        if (onProgressChange) onProgressChange(progress);

        if (progress >= 0.25)
            fireTrackingEvent(
                "firstQuartile",
                trackingEvents.find((e) => e.name === "firstQuartile")?.url
            );

        if (progress >= 0.5)
            fireTrackingEvent(
                "midpoint",
                trackingEvents.find((e) => e.name === "midpoint")?.url
            );

        if (progress >= 0.75)
            fireTrackingEvent(
                "thirdQuartile",
                trackingEvents.find((e) => e.name === "thirdQuartile")?.url
            );
    };

    const handlePlay = () => {
        setShowReplay(false);

        if (!hasStarted.current) {
            fireTrackingEvent(
                "start",
                trackingEvents.find((e) => e.name === "start")?.url
            );
            hasStarted.current = true;
        } else {
            fireTrackingEvent(
                "resume",
                trackingEvents.find((e) => e.name === "resume")?.url
            );
        }
    };

    const handlePause = () =>
        fireTrackingEvent(
            "pause",
            trackingEvents.find((e) => e.name === "pause")?.url
        );

    const handleEnded = () => {
        fireTrackingEvent(
            "complete",
            trackingEvents.find((e) => e.name === "complete")?.url
        );
        setShowReplay(true);
    };

    const handleError = () =>
        fireTrackingEvent(
            "Error",
            trackingEvents.find((e) => e.name === "Error")?.url
        );

    const handleVolumeChange = () => {
        if (!videoRef.current) return;

        if (videoRef.current.muted || videoRef.current.volume === 0) {
            fireTrackingEvent(
                "mute",
                trackingEvents.find((e) => e.name === "mute")?.url
            );
        } else {
            fireTrackingEvent(
                "unmute",
                trackingEvents.find((e) => e.name === "unmute")?.url
            );
        }
    };

    // 🔁 Automation: play → pause → resume → mute → unmute
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const startPlayback = async () => {
            try {
                await video.play();
                console.log("▶️ Auto-play started");

                setTimeout(() => {
                    video.pause();
                    console.log("⏸️ Auto-paused after 5s");

                    setTimeout(async () => {
                        await video.play();
                        console.log("▶️ Auto-resumed after 1s");

                        setTimeout(() => {
                            video.muted = true;
                            handleVolumeChange();
                            console.log("🔇 Auto-muted");

                            setTimeout(() => {
                                video.muted = false;
                                handleVolumeChange();
                                console.log("🔊 Auto-unmuted");
                            }, 2000);
                        }, 2000);
                    }, 1000);
                }, 5000);
            } catch (err) {
                console.warn("Auto-play failed:", err);
            }
        };

        fireTrackingEvent(
            "Impression",
            trackingEvents.find((e) => e.name === "Impression")?.url
        );

        startPlayback();
    }, []);

    // 🔄 Replay logic
    const handleReplay = () => {
        const video = videoRef.current;
        if (!video) return;

        firedEvents.current.clear();
        hasStarted.current = false;
        setShowReplay(false);

        video.currentTime = 0;
        video.muted = false;
        video.play().then(() => console.log("🔁 Replay started"));
    };

    return (
        <div className="relative w-full">
            <video
                ref={videoRef}
                src={src}
                controls
                className="w-full rounded-lg shadow-lg bg-black"
                onTimeUpdate={handleTimeUpdate}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onError={handleError}
                onVolumeChange={handleVolumeChange}
            />

            {showReplay && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                    <button
                        onClick={handleReplay}
                        className="px-6 py-3 bg-gradient-to-r from-slate-800 to-slate-700 text-white text-md font-semibold rounded-full shadow-2xl cursor-pointer transition-all duration-300"
                    >
                        Play Again
                    </button>
                </div>
            )}
        </div>
    );
};

export default VideoPlayer;
